import React from "react";
import ReactDOM from "react-dom/client";
import { createBrowserRouter, redirect, RouterProvider } from "react-router-dom";
import { ApolloClient, InMemoryCache, ApolloProvider } from "@apollo/client";
import LogRocket from "logrocket";
import setupLogRocketReact from "logrocket-react";

import { GET_PROJECT_WITH_GAMES } from "./graphql/queries/Project";
import { IntegrationApi } from "./integrations/IntegrationApi";

import "@datagame/ui/styles/application.scss";
import "./initializers/i18n";
import App from "./App";

const API_HOST = import.meta.env.VITE_API_HOST;
const IS_PRODUCTION = import.meta.env.PROD;
const LOGROCKET_APP_ID = import.meta.env.VITE_LOGROCKET_RESPONDER_APP_ID;

// Glaukos project IDs - update these to change redirect targets
export const GLAUKOS_PROJECT_ID_PRODUCTION = "88bba501-b52d-4726-af3a-7eb41cf25119";
export const GLAUKOS_PROJECT_ID_STAGING = "152c8f43-02fa-483d-ab8e-535efed74d3b";

const client = new ApolloClient({
  uri: `${API_HOST}/graphql`,
  cache: new InMemoryCache(),
});

export const integrationApi = IntegrationApi.create(window.__DATAGAME_CONFIGURATION);
integrationApi.hideNativeControls();

const router = createBrowserRouter(
  [
    {
      path: "/",
      element: <App />,
      loader: async () => {
        if (!integrationApi.projectId) return null;

        const response = await client.query({
          query: GET_PROJECT_WITH_GAMES,
          variables: {
            projectId: integrationApi.projectId,
          },
        });

        return response.data.projectPublic;
      },
    },
    // Glaukos redirect - this route is used for posters with the qr code pointing to this route to redirect to the project page for Glaukos
    {
      path: "/glaukos",
      loader: () => {
        const projectId = IS_PRODUCTION ? GLAUKOS_PROJECT_ID_PRODUCTION : GLAUKOS_PROJECT_ID_STAGING;
        return redirect(`/${projectId}`);
      },
    },
    {
      path: "/:projectId/*",
      element: <App />,
      loader: async ({ params }) => {
        if (!params.projectId) return null;

        const response = await client.query({
          query: GET_PROJECT_WITH_GAMES,
          variables: {
            projectId: params.projectId,
          },
        });

        return response.data.projectPublic;
      },
    },
  ],
  { basename: integrationApi.isEmbeeded ? window.location.pathname : "/" },
);

let rootContainer = document.getElementById("root") as HTMLElement;

if (integrationApi.isEmbeeded) {
  // eslint-disable-next-line
  // @ts-ignore
  rootContainer = window.__DATAGAME_SHADOW_ROOT;
}

if (IS_PRODUCTION) {
  LogRocket.init(LOGROCKET_APP_ID);
  setupLogRocketReact(LogRocket);
}

const root = ReactDOM.createRoot(rootContainer);

root.render(
  <ApolloProvider client={client}>
    <React.StrictMode>
      <RouterProvider router={router} />
    </React.StrictMode>
  </ApolloProvider>,
);
